import {
  LayoutTemplate,
  ModuleBucketSize,
  ModuleConnectionDisplayMode,
  ModuleConnectionLayout,
  ModuleConnectionSize,
  SessionModule,
  SessionModuleDisplaySettings,
} from '@creative-kit/shared';
import { RootStore } from 'logic';
import { action, makeObservable } from 'mobx';

export type UserModuleDisplaySettings = {
  connectionsSize: ModuleConnectionSize;
};

export function getPercentageForSize(size: ModuleBucketSize): number {
  switch (size) {
    case ModuleBucketSize.Full:
      return 100;
    case ModuleBucketSize.TwoThirds:
      return 75; // Changed from 66.666666 to 75
    case ModuleBucketSize.Half:
      return 50;
    case ModuleBucketSize.Compact:
      return 25; // Changed from 33.333333 to 25
    default:
      return 100;
  }
}

// Convert ModuleBucketSize to 12-column grid span (must match getPercentageForSize: 25%=3, 50%=6, 75%=9, 100%=12)
export function getColumnSpanForSize(size: ModuleBucketSize): number {
  switch (size) {
    case ModuleBucketSize.Full:
      return 12;
    case ModuleBucketSize.TwoThirds:
      return 9; // 75% = 9/12 (so 25%+75% = 3+9 = 12 fills the row)
    case ModuleBucketSize.Half:
      return 6; // 50% = 6/12
    case ModuleBucketSize.Compact:
      return 3; // 25% = 3/12
    default:
      return 12;
  }
}

export const getRemForSize = (size: ModuleConnectionSize): string => {
  switch (size) {
    case ModuleConnectionSize.Full:
      return '28rem';
    case ModuleConnectionSize.Half:
      return '22rem';
    case ModuleConnectionSize.Compact:
      return '18rem';
    default:
      return '';
  }
};

type ModuleDisplaySettings = {
  connectionsDisplayMode: ModuleConnectionDisplayMode;
  connectionsSize: ModuleConnectionSize;
  moduleBucketSize: ModuleBucketSize;
};

export class UiModuleConnectionDisplayStore {
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      setConnectionDisplayMode: action.bound,
      getConnectionDisplayMode: action.bound,
      getModuleBucketSize: action.bound,
      setModuleBucketSize: action.bound,
      getModuleRowSpan: action.bound,
      setModuleRowSpan: action.bound,
      setConnectionsSize: action.bound,
      getConnectionsSize: action.bound,
      setConnectionLayout: action.bound,
      getConnectionLayout: action.bound,
      applyLayoutTemplate: action.bound,
    });
  }

  defaultModuleDisplaySettings: ModuleDisplaySettings = {
    connectionsDisplayMode: ModuleConnectionDisplayMode.Grid,
    connectionsSize: ModuleConnectionSize.Full,
    moduleBucketSize: ModuleBucketSize.Full,
  };

  setConnectionDisplayMode(
    module: SessionModule,
    displayMode: ModuleConnectionDisplayMode,
    stepId?: string | null,
  ) {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return;

    const moduleDisplaySettings =
      currentSession.getEffectiveModuleDisplaySettings(module.id, stepId) ||
      this.defaultModuleDisplaySettings;
    const newSettings = { ...moduleDisplaySettings, connectionsDisplayMode: displayMode };

    if (stepId) {
      currentSession.setStepModuleDisplaySettings(stepId, module.id, newSettings);
    } else {
      currentSession.setModuleDisplaySettings(module.id, newSettings);
    }
  }

  getConnectionDisplayMode(moduleId: string, stepId?: string | null): ModuleConnectionDisplayMode {
    const { currentSession } = this.rootStore.sessionsStore;
    const defaultMode = this.defaultModuleDisplaySettings.connectionsDisplayMode;

    if (!currentSession) return defaultMode;

    return (
      currentSession.getEffectiveModuleDisplaySettings(moduleId, stepId)?.connectionsDisplayMode ||
      defaultMode
    );
  }

  setConnectionsSize(module: SessionModule, connectionsSize: ModuleConnectionSize) {
    this.rootStore.userSettingsStore.modifyUserSettings({
      [`connectionDisplaySettings.${module.id}`]: connectionsSize,
    });
  }

  getConnectionsSize(moduleId: string): ModuleConnectionSize {
    const { connectionDisplaySettings } = this.rootStore.userSettingsStore.userSettings;

    return (
      (connectionDisplaySettings && connectionDisplaySettings[moduleId]) ||
      ModuleConnectionSize.Full
    );
  }

  setModuleBucketSize(module: SessionModule, bucketSize: ModuleBucketSize, stepId?: string | null) {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return;

    const moduleDisplaySettings =
      currentSession.getEffectiveModuleDisplaySettings(module.id, stepId) ||
      this.defaultModuleDisplaySettings;
    const newSettings = { ...moduleDisplaySettings, moduleBucketSize: bucketSize };

    if (stepId) {
      currentSession.setStepModuleDisplaySettings(stepId, module.id, newSettings);
    } else {
      currentSession.setModuleDisplaySettings(module.id, newSettings);
    }
  }

  getModuleBucketSize(moduleId: string, stepId?: string | null): ModuleBucketSize {
    const { currentSession } = this.rootStore.sessionsStore;
    const defaultMode = this.defaultModuleDisplaySettings.moduleBucketSize;

    if (!currentSession) return defaultMode;

    return (
      currentSession.getEffectiveModuleDisplaySettings(moduleId, stepId)?.moduleBucketSize ||
      defaultMode
    );
  }

  setModuleRowSpan(module: SessionModule, rowSpan: number, stepId?: string | null) {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return;

    const moduleDisplaySettings =
      currentSession.getEffectiveModuleDisplaySettings(module.id, stepId) ||
      this.defaultModuleDisplaySettings;
    const newSettings = { ...moduleDisplaySettings, moduleRowSpan: rowSpan };

    if (stepId) {
      currentSession.setStepModuleDisplaySettings(stepId, module.id, newSettings);
    } else {
      currentSession.setModuleDisplaySettings(module.id, newSettings);
    }
  }

  getModuleRowSpan(moduleId: string, stepId?: string | null): number {
    const { currentSession } = this.rootStore.sessionsStore;

    if (!currentSession) return 1;

    return currentSession.getEffectiveModuleDisplaySettings(moduleId, stepId)?.moduleRowSpan || 1;
  }

  setConnectionLayout(
    module: SessionModule,
    layout: ModuleConnectionLayout | undefined,
    stepId?: string | null,
  ) {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return;

    const moduleDisplaySettings =
      currentSession.getEffectiveModuleDisplaySettings(module.id, stepId) ||
      this.defaultModuleDisplaySettings;
    const newSettings = { ...moduleDisplaySettings, connectionLayout: layout };

    if (stepId) {
      currentSession.setStepModuleDisplaySettings(stepId, module.id, newSettings);
    } else {
      currentSession.setModuleDisplaySettings(module.id, newSettings);
    }
  }

  getConnectionLayout(
    moduleId: string,
    stepId?: string | null,
  ): ModuleConnectionLayout | undefined {
    const { currentSession } = this.rootStore.sessionsStore;

    if (!currentSession) return undefined;

    return currentSession.getEffectiveModuleDisplaySettings(moduleId, stepId)?.connectionLayout;
  }

  applyLayoutTemplate(template: LayoutTemplate, stepId?: string | null) {
    const { currentSession } = this.rootStore.sessionsStore;
    if (!currentSession) return;

    const { visibleModules } = currentSession;
    const updates: Record<string, SessionModuleDisplaySettings> = {};

    visibleModules.forEach((module, index) => {
      // Cycle through template slots
      const slotIndex = index % template.slots.length;
      const slot = template.slots[slotIndex];

      // Get current settings and merge with new values
      const currentSettings =
        currentSession.getEffectiveModuleDisplaySettings(module.id, stepId) ||
        this.defaultModuleDisplaySettings;

      updates[module.id] = {
        ...currentSettings,
        moduleBucketSize: slot.size,
        moduleRowSpan: slot.rowSpan,
      };
    });

    // Apply all updates in a single Firestore operation
    if (stepId) {
      currentSession.batchSetStepModuleDisplaySettings(stepId, updates);
    } else {
      currentSession.batchSetModuleDisplaySettings(updates);
    }
  }
}
